import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// removed unused Avatar imports
import {
  Users,
  Crown,
  Verified,
  TrendingUp,
  Eye,
  Calendar,
  Loader2
} from "lucide-react";
import { creatorSpaceApi, type CreatorProfile } from '@/lib/api/creatorSpace';
import { useToast } from "@/components/ui/use-toast";
// removed unused date-fns imports
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { CharacterPlaceholder } from "@/components/CharacterPlaceholder";
import { BlurredMedia } from "@/components/BlurredMedia";
import { getImageUrl } from "@/lib/utils/image";
import { isVideoUrl } from "@/lib/media";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import { VStack, HStack, Grid } from '@/components/core';

interface CreatorListProps {
  className?: string;
}

export const CreatorList: React.FC<CreatorListProps> = ({ className }) => {
  const { t } = useTranslation('creatorSpace');
  const [creators, setCreators] = useState<CreatorProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState('');
  const [filterTier] = useState<string>('all');
  const [sortBy] = useState<string>('subscribers');
  const [subscribingCreatorId, setSubscribingCreatorId] = useState<number | null>(null);
  const [subscriptionStatuses, setSubscriptionStatuses] = useState<Record<number, boolean>>({});
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadCreators = async () => {
    try {
      setLoading(true);
      const creatorsData = await creatorSpaceApi.getCreators(0, 50);
      setCreators(creatorsData);
      
      // Load subscription status for each creator if user is logged in
      if (user) {
        const statuses: Record<number, boolean> = {};
        await Promise.all(creatorsData.map(async (creator) => {
          try {
            const isSubscribed = await creatorSpaceApi.isSubscribed(creator.id);
            statuses[creator.id] = isSubscribed;
          } catch (error) {
            console.error(`Error checking subscription for creator ${creator.id}:`, error);
            statuses[creator.id] = false;
          }
        }));
        setSubscriptionStatuses(statuses);
      }
    } catch (error) {
      console.error('Error loading creators:', error);
      toast({
        title: t('common:errors.error', 'Fehler'),
        description: t('profile.loadError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCreators();
  }, [user]);

  const handleSubscribe = async (creatorId: number) => {
    if (!user) {
      navigate('/auth?tab=login');
      return;
    }

    try {
      setSubscribingCreatorId(creatorId);
      await creatorSpaceApi.subscribeToCreator(creatorId);
      
      // Update creator subscriber count and subscription status locally
      setCreators(prev => prev.map(creator => 
        creator.id === creatorId 
          ? { 
              ...creator, 
              total_subscribers: creator.total_subscribers + 1,
              display_subscribers: (creator.display_subscribers ?? (creator.total_subscribers + (creator.simulated_subscribers || 0))) + 1
            }
          : creator
      ));
      setSubscriptionStatuses(prev => ({ ...prev, [creatorId]: true }));
      
      toast({
        title: t('subscription.success'),
        description: t('subscription.successDesc', { name: '' }), // Name is not available here easily without lookup, maybe generic message?
        variant: "default"
      });
      
      // Navigate to creator profile after successful subscription
      navigate(`/creator/${creatorId}`);
      
    } catch (error: any) {
      console.error('Subscription error:', error);
      
      let errorMessage = t('subscription.error');
      if (error?.response?.data?.detail) {
        if (error.response.data.detail.includes("Insufficient coins")) {
          errorMessage = t('subscription.errorCoins');
        } else if (error.response.data.detail.includes("already has active subscription")) {
          errorMessage = t('subscription.errorActive');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: t('subscription.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubscribingCreatorId(null);
    }
  };

  const filteredAndSortedCreators = creators
    .filter(creator => {
      const matchesSearch = creator.character?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           creator.character?.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTier = filterTier === 'all' || creator.tier === filterTier;
      return matchesSearch && matchesTier;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'subscribers':
          {
            const ad = (a.display_subscribers ?? a.total_subscribers) || 0;
            const bd = (b.display_subscribers ?? b.total_subscribers) || 0;
            return bd - ad;
          }
        case 'posts':
          return b.total_posts - a.total_posts;
        case 'earnings':
          return b.total_earnings - a.total_earnings;
        case 'price':
          return a.monthly_price - b.monthly_price;
        case 'name':
          return (a.character?.name || '').localeCompare(b.character?.name || '');
        default:
          return 0;
      }
    });

  // removed unused helpers

  if (loading) {
    return (
      <HStack justify="center" align="center" className="h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </HStack>
    );
  }

  return (
    <VStack gap="lg" className={className}>
      {/* Header */}
      <HStack justify="between" align="center" className="mb-token-lg">
        <h3 className="text-xl font-bold design-text-primary">{t('list.title')}</h3>
        <Button
          variant="glass"
          onClick={() => { void loadCreators(); }}
          disabled={loading}
        >
          {t('list.refresh')}
        </Button>
      </HStack>

      {/* Stats Overview */}
      <div className="design-card p-token-lg mb-token-lg">
        <h4 className="font-semibold design-text-primary mb-token-md">{t('list.stats.overview')}</h4>
        <Grid cols={2} colsMd={4} gap="md">
          <div className="text-center">
            <div className="text-2xl font-bold design-text-primary">{creators.length}</div>
            <div className="text-sm design-text-secondary">Creators</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold design-text-primary">
              {creators.filter(c => c.tier === 'premium').length}
            </div>
            <div className="text-sm design-text-secondary">Premium</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold design-text-primary">
              {creators.filter(c => c.tier === 'exclusive').length}
            </div>
            <div className="text-sm design-text-secondary">Exclusive</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold design-text-primary">
              {creators.filter(c => c.is_verified).length}
            </div>
            <div className="text-sm design-text-secondary">{t('list.stats.verified')}</div>
          </div>
        </Grid>
      </div>

      {/* Creator Grid */}
      {filteredAndSortedCreators.length === 0 ? (
        <div className="design-card p-12 text-center">
          <Users className="h-16 w-16 mx-auto mb-token-md design-text-secondary" />
          <h4 className="text-lg font-semibold design-text-primary mb-token-sm">{t('list.noCreators')}</h4>
          <p className="design-text-secondary">
            {searchTerm || filterTier !== 'all' 
              ? t('list.noCreatorsFiltered')
              : t('list.noCreatorsAvailable')
            }
          </p>
        </div>
      ) : (
        <Grid cols={1} colsMd={2} colsLg={3} colsXl={4} gap="lg" className="stagger-children auto-rows-fr">
          {filteredAndSortedCreators.map((creator) => (
            <CreatorCard
              key={creator.id}
              creator={creator}
              onClick={() => navigate(`/creator/${creator.id}`)}
              user={user}
              onSubscribe={handleSubscribe}
              isSubscribing={subscribingCreatorId === creator.id}
              isSubscribed={subscriptionStatuses[creator.id] || false}
            />
          ))}
        </Grid>
      )}
    </VStack>
  );
};

interface CreatorCardProps {
  creator: CreatorProfile;
  onClick: () => void;
  user: any;
  onSubscribe: (creatorId: number) => Promise<void>;
  isSubscribing: boolean;
  isSubscribed: boolean;
}

const CreatorCard: React.FC<CreatorCardProps> = ({ creator, onClick, user, onSubscribe, isSubscribing, isSubscribed }) => {
  const { t } = useTranslation('creatorSpace');
  const ageVerified = user?.is_adult_verified || false;
  
  // Quelle wählen: 1) Profil-Galerie-Item, sonst 2) avatar_url (kann Video sein), sonst 3) erstes Galerie-Item
  const galleryItems = creator.character?.gallery || [];
  const profileItem = galleryItems.find((img: any) => img.is_profile_image);
  let mediaSrc: string | null = null;
  let shouldBlurImage = false;
  let isProfileImageNsfw = false;
  let posterUrl: string | undefined = undefined;
  let isVideoProfile = false;

  if (profileItem) {
    mediaSrc = getImageUrl(profileItem.url);
    shouldBlurImage = Boolean((profileItem as any)?.should_blur);
    isProfileImageNsfw = Boolean((profileItem as any)?.is_fsk18);
    posterUrl = (profileItem as any)?.thumbnail_url as string | undefined;
    isVideoProfile = ((profileItem as any)?.media_type === 'video') || isVideoUrl(mediaSrc);
  } else if (creator.character?.avatar_url) {
    mediaSrc = getImageUrl(creator.character.avatar_url);
    isVideoProfile = isVideoUrl(mediaSrc);
    // keine Blur/NSFW-Flags für Avatar verfügbar → standardmäßig false
  } else if (galleryItems[0]) {
    mediaSrc = getImageUrl((galleryItems[0] as any).url);
    shouldBlurImage = Boolean((galleryItems[0] as any)?.should_blur);
    isProfileImageNsfw = Boolean((galleryItems[0] as any)?.is_fsk18);
    posterUrl = (galleryItems[0] as any)?.thumbnail_url as string | undefined;
    isVideoProfile = ((galleryItems[0] as any)?.media_type === 'video') || isVideoUrl(mediaSrc);
  }

  return (
    <Card
      className={cn(
        "character-card cursor-pointer h-full",
        creator.tier === 'premium' && "premium-card",
        creator.tier === 'exclusive' && "exclusive-card"
      )}
      onClick={onClick}
    >
      {/* Creator Image */}
      <div className="character-card-image">
        {mediaSrc ? (
          <BlurredMedia
            src={mediaSrc}
            alt={creator.character?.name || 'Creator'}
            shouldBlur={shouldBlurImage}
            isFsk18={isProfileImageNsfw}
            className="w-full h-full object-cover object-center-top"
            autoPlayVideo
            showMuteToggle={false}
            useViewportAutoplay
            loopVideo
            {...(posterUrl ? { poster: posterUrl } : {})}
            {...(isVideoProfile ? { forceVideo: true } : {})}
          />
        ) : (
          <CharacterPlaceholder name={creator.character?.name || 'Creator'} />
        )}
        
        {/* Creator Info Overlay */}
        <div className="character-card-overlay">
          <HStack justify="between" align="center" className="mb-token-sm">
            <h3 className="text-2xl font-bold">{creator.character?.name || 'Creator'}</h3>
            {creator.is_verified && (
              <Verified className="h-5 w-5 text-info" />
            )}
          </HStack>
          <p className="text-muted text-sm opacity-90">
            {creator.tier === 'basic' ? 'Basic Creator' : 
             creator.tier === 'premium' ? 'Premium Creator' : 'Exclusive Creator'}
          </p>
          <HStack gap="sm" align="center" className="mt-token-sm text-sm">
            <span className="text-white/90">{Math.round(creator.monthly_price)} Coins/Monat</span>
            <span className="text-white/60">•</span>
            <span className="text-white/90">{(creator.display_subscribers ?? creator.total_subscribers)} Subscriber</span>
          </HStack>
        </div>
        
        {/* Tier & Verified Badges */}
        <VStack gap="sm" className="absolute top-token-md right-token-md">
          {creator.tier === 'premium' && (
            <Badge className="bg-warning text-warning-foreground font-semibold border-warning">
              <Crown className="h-3 w-3 mr-token-xs" />
              Premium
            </Badge>
          )}
          {creator.tier === 'exclusive' && (
            <Badge className="bg-brand-secondary text-white font-semibold border-brand-secondary">
              <Crown className="h-3 w-3 mr-token-xs" />
              Exclusive
            </Badge>
          )}
        </VStack>
      </div>
      
      {/* Creator Content */}
      <CardContent className="character-card-content flex-1">
        <p className="text-muted-foreground text-sm line-clamp-3 mt-token-md mb-token-md leading-relaxed">
          {creator.character?.description || t('profile.noDescription')}
        </p>
        
        {/* Creator Stats */}
        <HStack gap="md" wrap className="mb-token-lg text-sm">
          <HStack gap="sm" align="center" className="text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{creator.total_posts} {t('card.posts')}</span>
          </HStack>
          <HStack gap="sm" align="center" className="text-muted-foreground">
            <Eye className="h-4 w-4" />
            <span>{(creator.display_subscribers ?? creator.total_subscribers)} {t('card.fans')}</span>
          </HStack>
          {creator.total_earnings > 0 && (
            <HStack gap="sm" align="center" className="text-success">
              <TrendingUp className="h-4 w-4" />
              <span>{t('card.topCreator')}</span>
            </HStack>
          )}
        </HStack>
        
        {/* Buttons Section - Fixed height for consistency */}
        <VStack gap="sm" className="mt-auto min-h-[120px] justify-end">
          {user ? (
            <>
              <Button 
                variant={isSubscribed ? "glass" : "gradient-primary"}
                className="w-full h-12 font-semibold text-sm leading-tight"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSubscribed) {
                    void onSubscribe(creator.id);
                  }
                }}
                disabled={isSubscribing || isSubscribed}
              >
                {isSubscribing ? (
                  <>
                    <Loader2 className="mr-token-sm h-4 w-4 animate-spin flex-shrink-0" />
                    <span className="truncate">{t('card.subscribing')}</span>
                  </>
                ) : isSubscribed ? (
                  <>
                    <Crown className="mr-token-sm h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{t('card.subscribed')}</span>
                  </>
                ) : (
                  <>
                    <Eye className="mr-token-sm h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{t('card.subscribe')} ({Math.round(creator.monthly_price)} Coins/Monat)</span>
                  </>
                )}
              </Button>
              <Button 
                variant="glass" 
                className="w-full h-12 font-semibold text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                <span className="truncate">{t('card.viewProfile')}</span>
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="gradient" 
                className="w-full h-12 font-semibold text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/auth?tab=register';
                }}
              >
                <Eye className="mr-token-sm h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('card.subscribeNow')}</span>
              </Button>
              <Button 
                variant="glass" 
                className="w-full h-12 font-semibold text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/auth?tab=login';
                }}
              >
                <span className="truncate">{t('card.login')}</span>
              </Button>
            </>
          )}
        </VStack>
      </CardContent>
    </Card>
  );
};
